import React, { memo, useCallback, useMemo, useState } from 'react';
import {
  Typography,
  useI18nContext,
  Switch,
  Tooltip,
} from '@procore/core-react';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import { ExpandLess, ExpandMore, Help } from '@mui/icons-material';
import { FilterState } from '../../helpers/filters';
import { useStyles } from './EnhancedFilters.styles';
import { FiltersLists } from '../../services/business-registry';
import FilterCheckboxes, { FilterCheckboxesProps } from './FilterCheckboxes';
import { getTradesToUpdate } from '../utils/tradesBulkCheckboxUtil';
import { BreadcrumbItem } from '../Breadcrumbs/BreadcrumbContainer';
import AnalyticsWrappedComponent from '../Analytics/AnalyticsWrappedComponent';

interface FilterSectionProps extends FilterCheckboxesProps {
  sectionName: string;
}

const SearchByOfficeAddressToggle = memo(
  ({ value, update }: { value: boolean; update: any }) => {
    const I18n = useI18nContext();
    const classes = useStyles();

    return (
      <div className={classes.onlyOfficeAddressContainer}>
        <AnalyticsWrappedComponent
          componentType={Switch}
          eventText="Clicked Only Office Address Toggle"
          eventLabel="Only Office Address Toggle"
          additionalEventProperties={{
            View: 'desktop',
            Value: value,
          }}
          id="only-office-address"
          checked={value}
          onChange={() => {
            update(!value);
          }}
        />
        <label
          className={classes.onlyOfficeAddressText}
          htmlFor="only-office-address"
          suppressHydrationWarning
        >
          {I18n.t('components.only_office_address.label')}
        </label>
        <Tooltip
          overlay={I18n.t('components.only_office_address.tooltip')}
          trigger="hover"
        >
          <Help className={classes.onlyOfficeAddressTooltip} />
        </Tooltip>
      </div>
    );
  }
);

const FilterSection = memo(
  ({
    sectionName,
    updateCheckbox,
    FiltersList,
    selectedFilters,
    dataTrackClick,
    analyticsEventText,
    showSearch,
    sublistPropertyName,
  }: FilterSectionProps) => {
    const [expanded, setExpanded] = useState(true);
    const classes = useStyles();
    const selectedFiltersCount = selectedFilters.length;

    return (
      <Box className={classes.container} data-test-id={`filter-${sectionName}`}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          onClick={() => setExpanded(!expanded)}
          className={classes.header}
        >
          <Typography
            intent="body"
            weight="semibold"
            data-test-id="section-name"
          >
            {selectedFiltersCount > 0
              ? `${sectionName} (${String(selectedFiltersCount)})`
              : `${sectionName}`}
          </Typography>

          {expanded ? <ExpandLess /> : <ExpandMore />}
        </Box>

        <Collapse in={expanded}>
          <Box className={classes.checkboxContainer}>
            <FilterCheckboxes
              sectionName={sectionName}
              updateCheckbox={updateCheckbox}
              FiltersList={FiltersList}
              selectedFilters={selectedFilters}
              dataTrackClick={dataTrackClick}
              analyticsEventText={analyticsEventText}
              showSearch={showSearch}
              sublistPropertyName={sublistPropertyName}
            />
          </Box>
        </Collapse>
      </Box>
    );
  }
);
FilterSection.displayName = 'FilterSection';

interface FiltersProps {
  useFilterHandlers: FilterState;
  filtersLists: FiltersLists;
  breadcrumbs: Array<BreadcrumbItem>;
}

const EnhancedFilters = memo(
  ({ useFilterHandlers, filtersLists, breadcrumbs }: FiltersProps) => {
    const I18n = useI18nContext();
    const {
      filters,
      updateCheckbox,
      updateCheckboxBulk,
      updateOnlyOfficeAddress,
    } = useFilterHandlers;
    const classes = useStyles();

    const updateCheckboxMemo = useMemo(() => updateCheckbox, []);
    const updateTradesMemo = useCallback(
      (item) => {
        const { checked, trades } = getTradesToUpdate(
          item,
          filtersLists.trades,
          filters.trade
        );
        updateCheckboxBulk(trades, checked);
      },
      [filtersLists.trades, filters.trade]
    );

    return (
      <Box
        display="flex"
        flexDirection="column"
        className={classes.outerContainer}
      >
        {breadcrumbs.length > 0 && (
          <SearchByOfficeAddressToggle
            value={filters.onlyOfficeAddress}
            update={updateOnlyOfficeAddress}
          />
        )}
        <FilterSection
          sectionName={I18n.t('components.filters.market_sector')}
          updateCheckbox={updateCheckboxMemo}
          FiltersList={filtersLists.constructionSectors}
          selectedFilters={filters.constructionSector}
          dataTrackClick="Construction Sector Filters"
          analyticsEventText="Clicked Market Sector Filter Checkbox"
        />

        <FilterSection
          sectionName={I18n.t('components.filters.company_type')}
          updateCheckbox={updateCheckboxMemo}
          FiltersList={filtersLists.companyTypes}
          selectedFilters={filters.companyType}
          dataTrackClick="Company Type Options"
          analyticsEventText="Clicked Company Type Filter Checkbox"
        />

        <FilterSection
          sectionName={I18n.t('components.filters.business_classification')}
          updateCheckbox={updateCheckboxMemo}
          FiltersList={filtersLists.classifications}
          selectedFilters={filters.classifications}
          dataTrackClick="Business Classification Options"
          analyticsEventText="Clicked Business Classification Filter Checkbox"
        />

        <FilterSection
          sectionName={I18n.t('components.filters.trades_and_services')}
          updateCheckbox={updateTradesMemo}
          FiltersList={filtersLists.trades}
          selectedFilters={filters.trade}
          dataTrackClick="Trade Filters"
          analyticsEventText="Clicked Trade Filter Checkbox"
          showSearch
          sublistPropertyName="specialties"
        />
      </Box>
    );
  }
);
EnhancedFilters.displayName = 'EnhancedFilters';

export default EnhancedFilters;
